import { lazy, Suspense } from "react";
import { Toaster, TooltipProvider } from "@cieda/ui";
import { Toaster as SonnerToaster } from "sonner";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from "@vercel/analytics/react";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ErrorBoundary from "@/components/ErrorBoundary";
// import AuthGuard from "./components/auth/AuthGuard"; // Disabled — storefront is now vitrine-only
// Sécurité de session : le SessionTimeoutWarning s'active automatiquement
// lorsque les routes authentifiées sont décommentées (mode e-commerce actif)
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import CookieConsent from "@/components/CookieConsent";
import { ChatWidget } from "@/components/chat/ChatWidget";
import { ff } from "@/lib/feature-flags";
import { PageSkeleton } from "@/components/ui/Skeleton";
import DjaymaRedirect from "@/components/DjaymaRedirect";

// ──────────────────────────────────────────────────────────────────
// Lazy-loaded pages — reduces initial bundle for public visitors
// ──────────────────────────────────────────────────────────────────

// DISABLED — e-commerce pages now redirect to djayma.com (vitrine mode)
// Keep imports commented out so they can be re-enabled later.
// const Cart = lazy(() => import("./pages/Cart"));
// const Livraison = lazy(() => import("./pages/Livraison"));
// const Checkout = lazy(() => import("./pages/Checkout"));
// const Tracking = lazy(() => import("./pages/Tracking"));
// const Login = lazy(() => import("./pages/account/Login"));
// const Register = lazy(() => import("./pages/account/Register"));
// const ResetRequest = lazy(() => import("./pages/account/ResetRequest"));
// const ResetPassword = lazy(() => import("./pages/account/ResetPassword"));
// const Profile = lazy(() => import("./pages/account/Profile"));
// const Orders = lazy(() => import("./pages/account/Orders"));
// const Addresses = lazy(() => import("./pages/account/Addresses"));
// const Returns = lazy(() => import("./pages/account/Returns"));

// Active lazy-loaded pages
const PolitiqueConfidentialite = lazy(() => import("./pages/PolitiqueConfidentialite"));
const ConditionsGeneralesVente = lazy(() => import("./pages/ConditionsGeneralesVente"));
const MentionsLegales = lazy(() => import("./pages/MentionsLegales"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 10 * 60_000,
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

// Skeleton page loader (Amazon-style) or minimal spinner fallback
const PageLoader = () =>
  ff.skeletonScreens ? (
    <PageSkeleton />
  ) : (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-secondary border-t-transparent" />
    </div>
  );

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <SonnerToaster />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* ── Active pages ──────────────────────────────── */}
              <Route path="/" element={<Index />} />
              <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
              <Route path="/conditions-generales-vente" element={<ConditionsGeneralesVente />} />
              <Route path="/mentions-legales" element={<MentionsLegales />} />

              {/* ── Disabled e-commerce routes → redirect to Djayma ── */}
              <Route path="/panier" element={<DjaymaRedirect to="https://djayma.com/panier" label="votre panier" />} />
              <Route path="/livraison" element={<DjaymaRedirect to="https://djayma.com/livraison" label="la livraison" />} />
              <Route path="/commande" element={<DjaymaRedirect to="https://djayma.com/checkout" label="le paiement" />} />
              <Route path="/connexion" element={<DjaymaRedirect to="https://djayma.com/account/login" label="la connexion" />} />
              <Route path="/inscription" element={<DjaymaRedirect to="https://djayma.com/account/register" label="l'inscription" />} />
              <Route path="/reinitialisation" element={<DjaymaRedirect to="https://djayma.com/account/reset-request" label="la reinitialisation" />} />
              <Route path="/reinitialisation/nouveau" element={<DjaymaRedirect to="https://djayma.com/account/reset-password" label="la reinitialisation" />} />
              <Route path="/compte" element={<DjaymaRedirect to="https://djayma.com/account/profile" label="votre compte" />} />
              <Route path="/compte/commandes" element={<DjaymaRedirect to="https://djayma.com/account/orders" label="vos commandes" />} />
              <Route path="/compte/adresses" element={<DjaymaRedirect to="https://djayma.com/account/addresses" label="vos adresses" />} />
              <Route path="/compte/retours" element={<DjaymaRedirect to="https://djayma.com/account/returns" label="vos retours" />} />

              {/* DISABLED — original e-commerce routes (kept for reference)
              <Route path="/panier" element={<Cart />} />
              <Route path="/livraison" element={<Livraison />} />
              <Route path="/commande" element={<Checkout />} />
              <Route path="/suivi/:ref" element={<Tracking />} />
              <Route path="/connexion" element={<Login />} />
              <Route path="/inscription" element={<Register />} />
              <Route path="/reinitialisation" element={<ResetRequest />} />
              <Route path="/reinitialisation/nouveau" element={<ResetPassword />} />
              <Route path="/compte" element={<AuthGuard><Profile /></AuthGuard>} />
              <Route path="/compte/commandes" element={<AuthGuard><Orders /></AuthGuard>} />
              <Route path="/compte/adresses" element={<AuthGuard><Addresses /></AuthGuard>} />
              <Route path="/compte/retours" element={<AuthGuard><Returns /></AuthGuard>} />
              */}

              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <CookieConsent />
          <ChatWidget />
        </BrowserRouter>
        <SpeedInsights />
        <Analytics />
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
